import { client as apollo } from "@/graphql/vue-apollo";
import shopQuery from "@/graphql/queries/Shop.gql";
import menuQuery from "@/graphql/queries/Menu.gql";
import cloneDeep from "lodash-es/cloneDeep";
import isEmpty from "lodash-es/isEmpty";
import scrollLock from "@/helpers/scroll-lock";
import { localeUrls } from "@/config";
import { currencyToCountry } from "@/helpers/countries";
import * as Sentry from "@sentry/vue";

const state = {
  isBurgerMenuOpen: false,
  isNavOpen: false,
  userPreferences: {},
  recentlyViewedProducts: [],
  userCountry: null,
  isSlideLoginOpen: false,
  isSlideSignUpOpen: false,
  shop: {},
  isAccountOpen: false,
  shopCountryCode: null,
  navigationBarLinks: [],
  footerLeftLinks: [],
  footerRightLinks: [],
  deliveryEstimate: {
    estimated_delivery_date: null,
    estimated_delivery_date_min: null,
    estimated_delivery_date_max: null,
    service_type_name: null,
  },
};

const mutations = {
  toggleBurgerMenuState(state) {
    state.isBurgerMenuOpen = !state.isBurgerMenuOpen;
  },
  setUserPreferences(state, value) {
    state.userPreferences = value;
  },
  setRecentlyViewedProducts(state, value) {
    state.recentlyViewedProducts = value;
  },
  setUserCountry(state, value) {
    state.userCountry = value;
  },
  setSlideLoginState(state, value) {
    state.isSlideLoginOpen = value;
  },
  setSlideSignUpState(state, value) {
    state.isSlideSignUpOpen = value;
  },
  setShopState(state, value) {
    state.shop = value;
  },
  setShopCountryCodeState(state, value) {
    state.shopCountryCode = value;
  },
  setAccountOpenState(state, value) {
    state.isAccountOpen = value;
  },
  setNavOpenState(state, value) {
    state.isNavOpen = value;
  },
  setNavigationLinks(state, value) {
    state.navigationBarLinks = value;
  },
  setFooterLeftLinks(state, value) {
    state.footerLeftLinks = value;
  },
  setFooterRightLinks(state, value) {
    state.footerRightLinks = value;
  },
  setDeliveryEstimate(state, value) {
    state.deliveryEstimate = value;
  },
};

const getters = {
  collectionPageLayout: (state) => {
    if (isEmpty(state.userPreferences)) {
      return "grid";
    }
    return state.userPreferences["collectionPageLayout"] || "grid";
  },
  storeRedirectDismissed: (state) => {
    if (isEmpty(state.userPreferences)) {
      return false;
    }
    return state.userPreferences["storeRedirectDismissed"] || false;
  },
  storeAutoRedirected: (state) => {
    if (isEmpty(state.userPreferences)) {
      return false;
    }
    return state.userPreferences["storeAutoRedirected"] || false;
  },
  currencyCode: (state) => {
    return state.shop?.paymentSettings?.currencyCode.toLowerCase() || null;
  },
  moneyFormat: (state) => {
    return state.shop?.moneyFormat || "£{{amount}}";
  },
  slideSignUpTimedDismissed: (state) => {
    if (isEmpty(state.userPreferences)) {
      return false;
    }
    return state.userPreferences["slideSignUpTimedDismissed"] || false;
  },
  userPostCode: (state) => {
    if (isEmpty(state.userPreferences)) {
      return null;
    }
    return state.userPreferences["userPostCode"] || null;
  },
};

const actions = {
  getUserPreferences({ commit }) {
    const userPreferences = localStorage.getItem("shopifyUserPreferences");
    commit("setUserPreferences", JSON.parse(userPreferences));
  },
  getRecentlyViewedProducts({ commit }) {
    const recentlyViewed = localStorage.getItem(
      "shopifyRecentlyViewedProducts"
    );

    if (!recentlyViewed) {
      return;
    }

    let jsonRecentlyViewed = JSON.parse(recentlyViewed) || [];
    jsonRecentlyViewed.sort((a, b) => Date.parse(b.date) - Date.parse(a.date));

    const numberOfItemsToRemove = 10 - jsonRecentlyViewed.length;

    if (numberOfItemsToRemove < 0) {
      jsonRecentlyViewed = jsonRecentlyViewed.slice(0, numberOfItemsToRemove);
    }

    commit("setRecentlyViewedProducts", jsonRecentlyViewed);
  },
  async getUserCountry({ commit }) {
    const response = await fetch(
      "https://pro.ip-api.com/json?key=Y6SLxPFTt7gdeOe"
    );
    const { countryCode } = await response.json();
    commit("setUserCountry", countryCode);
  },
  toggleMobileMenu({ commit, state }) {
    commit("toggleBurgerMenuState");
    state.isBurgerMenuOpen ? scrollLock.enable() : scrollLock.disable();
  },
  setSlideLogin({ commit }, value) {
    commit("setSlideLoginState", value);
  },
  setSlideSignUp({ commit }, value) {
    commit("setSlideSignUpState", value);
  },
  setUserPreferences({ commit, state }, { key, value }) {
    const userPreferences = cloneDeep(state.userPreferences) || {};
    userPreferences[key] = value;

    localStorage.setItem(
      "shopifyUserPreferences",
      JSON.stringify(userPreferences)
    );

    commit("setUserPreferences", userPreferences);
  },
  setRecentlyViewedProducts({ commit, state }, productId) {
    const recentlyViewed = cloneDeep(state.recentlyViewedProducts) || [];

    recentlyViewed.push({
      id: productId,
      date: new Date(),
    });

    localStorage.setItem(
      "shopifyRecentlyViewedProducts",
      JSON.stringify(recentlyViewed)
    );

    commit("setRecentlyViewedProducts", recentlyViewed);
  },
  async getShopInfo({ commit, dispatch }) {
    const { data } = await apollo.query({
      query: shopQuery,
    });

    commit("setShopState", data.shop);
    dispatch("setShopCountryCode");
  },
  setShopCountryCode({ commit, getters }) {
    const countryCode = currencyToCountry[getters.currencyCode];

    commit("setShopCountryCodeState", countryCode);
  },
  changeCurrency(_, currency) {
    const url = localeUrls[currency];
    const currentPath = window.location.pathname;
    const nostoRemovedPath = currentPath.split(/-\d/)[0];

    if (url) {
      window.location.href = "https://" + url + nostoRemovedPath;
    }
  },
  setAccountOpen({ commit }, value) {
    commit("setAccountOpenState", value);
  },
  setNavOpen({ commit }, value) {
    commit("setNavOpenState", value);
  },
  async getNavigationLinks({ commit }) {
    const { data } = await apollo.query({
      query: menuQuery,
      variables: {
        handle: "navigation-bar",
      },
    });

    commit("setNavigationLinks", data.menu.items);
  },
  async getFooterLeftLinks({ commit }) {
    const { data } = await apollo.query({
      query: menuQuery,
      variables: {
        handle: "footer-left",
      },
    });

    if (data.menu?.items) {
      commit("setFooterLeftLinks", data.menu.items);
    }
  },
  async getFooterRightLinks({ commit }) {
    const { data } = await apollo.query({
      query: menuQuery,
      variables: {
        handle: "footer-right",
      },
    });

    if (data.menu?.items) {
      commit("setFooterRightLinks", data.menu.items);
    }
  },
  async getDeliveryEstimate(
    { state, getters, commit },
    { weight, estimatePostcode = false }
  ) {
    let postcode = null;

    if (getters.userPostCode) {
      postcode = getters.userPostCode;

      Sentry.addBreadcrumb({
        category: "delivery-estimate",
        message: "Actual postcode: " + postcode,
        level: "info",
      });
    } else if (estimatePostcode) {
      const response = await fetch(
        "https://pro.ip-api.com/json?key=Y6SLxPFTt7gdeOe"
      );
      const { zip: usersEstimatedPostCode } = await response.json();
      postcode = usersEstimatedPostCode;

      Sentry.addBreadcrumb({
        category: "delivery-estimate",
        message: "Estimated postcode: " + postcode,
        level: "info",
      });
    }

    if (!postcode) {
      console.error("Delivery Estimate: No postcode provided");
      return;
    }

    const requestBody = {
      postcode,
      country: state.userCountry,
      weight,
    };

    const response = await fetch(
      process.env.RATANDBOA_NODE_APP_URL + "/delivery-estimate",
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      Sentry.captureMessage("Something went wrong", response);
      return;
    }

    const json = await response.json();

    Sentry.addBreadcrumb({
      category: "delivery-estimate",
      message: "Request and Response",
      data: {
        requestBody,
        responseJson: json,
      },
      level: "info",
    });

    const {
      estimated_delivery_date,
      estimated_delivery_date_min,
      estimated_delivery_date_max,
      service_type_name,
    } = json;

    commit("setDeliveryEstimate", {
      estimated_delivery_date,
      estimated_delivery_date_min,
      estimated_delivery_date_max,
      service_type_name,
    });
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
